import React from 'react';

import { formatDateTime } from 'utils/dateUtils';
import {
  cleanEmailBody,
  formatAttachmentSize,
  formatEmailRecipient,
  formatEmailSender,
  hasAttachments,
  parseEmailLinks,
  parseEmailMessage,
} from 'utils/emailUtils';

import {
  AttachmentIconSvg,
  AttachmentInfo,
  AttachmentItem,
  AttachmentName,
  AttachmentPreview,
  AttachmentSize,
  AttachmentsSection,
  EmailBody,
  EmailBodyLink,
  EmailBodyText,
  EmailContainer,
  EmailHeader,
  EmailHeaderLabel,
  EmailHeaderRow,
  EmailHeaderValue,
  EmailSubject,
} from './EmailMessage.styles';

const getFileExtension = (filename) => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isImageFile = (filename) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
  const ext = getFileExtension(filename);
  return imageExtensions.includes(ext);
};

const renderFileIcon = (filename) => {
  const isImage = isImageFile(filename);

  if (isImage) {
    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"
        fill="currentColor"
      />
    </svg>
  );
};

/**
 * EmailMessage Component
 * Displays IMAP email messages in a properly formatted way
 */
const EmailMessage = ({ message, isMyMessage = false }) => {
  if (!message) return null;

  // Parse the email message
  const emailData = parseEmailMessage(message);

  if (!emailData) return null;

  const { subject, body, sender, recipient, date, attachments } = emailData;

  // Parse body to make links clickable
  const bodyParts = parseEmailLinks(cleanEmailBody(body));

  return (
    <EmailContainer $isMyMessage={isMyMessage}>
      {/* Email Subject */}
      {subject && <EmailSubject>{subject}</EmailSubject>}

      {!isMyMessage && (
        <EmailHeader>
          <EmailHeaderRow>
            <EmailHeaderLabel>From:</EmailHeaderLabel>
            <EmailHeaderValue>{formatEmailSender(sender)}</EmailHeaderValue>
          </EmailHeaderRow>

          <EmailHeaderRow>
            <EmailHeaderLabel>To:</EmailHeaderLabel>
            <EmailHeaderValue>{formatEmailRecipient(recipient)}</EmailHeaderValue>
          </EmailHeaderRow>

          <EmailHeaderRow>
            <EmailHeaderLabel>Date:</EmailHeaderLabel>
            <EmailHeaderValue>
              {formatDateTime(date, false, false, 'MMM D, YYYY [at] h:mm A')}
            </EmailHeaderValue>
          </EmailHeaderRow>
        </EmailHeader>
      )}

      {/* Email Body */}
      {body && (
        <EmailBody>
          <EmailBodyText>
            {bodyParts.map((part, index) => {
              if (part.type === 'link') {
                return (
                  <EmailBodyLink
                    key={index}
                    href={part.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {part.content}
                  </EmailBodyLink>
                );
              }
              return <span key={index}>{part.content}</span>;
            })}
          </EmailBodyText>
        </EmailBody>
      )}

      {/* Attachments */}
      {hasAttachments(message) && (
        <AttachmentsSection>
          <EmailHeaderLabel>Attachments ({attachments.length}):</EmailHeaderLabel>
          {attachments.map((attachment, index) => {
            const fileName =
              attachment.filename || attachment.name || `Attachment ${index + 1}`;
            const fileSize = attachment.size || 0;
            const fileUrl = attachment.url || attachment.downloadUrl || '#';

            const handleDownload = (e) => {
              if (fileUrl && fileUrl !== '#') {
                return;
              }
              e.preventDefault();
            };

            return (
              <AttachmentItem key={index} onClick={handleDownload}>
                <AttachmentPreview>
                  <AttachmentIconSvg>{renderFileIcon(fileName)}</AttachmentIconSvg>
                </AttachmentPreview>
                <AttachmentInfo>
                  <AttachmentName
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={fileName}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {fileName}
                  </AttachmentName>
                  {fileSize > 0 && (
                    <AttachmentSize>{formatAttachmentSize(fileSize)}</AttachmentSize>
                  )}
                </AttachmentInfo>
              </AttachmentItem>
            );
          })}
        </AttachmentsSection>
      )}
    </EmailContainer>
  );
};

export default EmailMessage;
